import type { EditorEvents } from '@tiptap/react'
import { useEffect, useState } from 'react'
import getFeatureEditor from '@chegg-wt/core-wa/utils/getEditor'
import { getSelection } from '@chegg-wt/core-wa/utils/helpers'
import { useDebounce } from '@chegg-wt/core/hooks/functions'
import { EditorSelection } from '../types'

export const SELECTION_DELAY_IN_MS = 300

/**
 * React state synced current editor selection
 *
 * @returns {(EditorSelection | null)}
 */
const useEditorSelection = (): EditorSelection | null => {
  const editor = getFeatureEditor()
  const [editorSelection, setEditorSelection] = useState(
    getSelection(editor?.view?.state)
  )

  const debouncedSetEditorSelection = useDebounce((updatedSelection: EditorSelection) => {
    // Update when new and old selection is differnt and also when text is unselected or whitespace is selected
    if (
      editorSelection.selectedText !== updatedSelection.selectedText ||
      editorSelection.from !== updatedSelection.from
    ) {
      setEditorSelection(updatedSelection)
    }
  }, SELECTION_DELAY_IN_MS)

  useEffect(() => {
    const onUpdate = ({
      editor: updatedEditor,
    }: EditorEvents['selectionUpdate']): void => {
      const updatedSelection = getSelection(updatedEditor.view.state)
      debouncedSetEditorSelection(updatedSelection)
    }
    editor?.on('selectionUpdate', onUpdate)
    return () => {
      editor?.off('selectionUpdate', onUpdate)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor])

  return editorSelection
}

export default useEditorSelection
